import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type Defect, buildAutoDefectTitle } from '@testflow/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { catalogApi } from '../../lib/catalog-api.js';
import { defectsApi } from '../../lib/defects-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import { defaultActivePlanId } from '../../lib/plan-selection.js';
import { plansApi } from '../../lib/plans-api.js';
import { runsApi } from '../../lib/runs-api.js';

// #39: manuelles Anlegen. Bis hierher konnte ein Defect ausschliesslich aus einem Fehler-Lauf
// entstehen — ein Fund im Workshop oder beim Review hatte keinen Weg ins System.
//
// Der Testfall-Bezug ist bewusst optional: genau die Faelle, fuer die dieser Dialog da ist,
// haben oft gar keinen Testfall.
//
// #208: Mit `stepContext` legt der Tester aus der Ausfuehrung einen Defect zu genau einem Schritt an
// (ohne Ergebnis zu buchen). Testfall und Plan stehen dann fest; Katalog- und Plan-Listen duerfen
// Tester nicht lesen und werden deshalb gar nicht erst abgefragt.
export interface DefectStepContext {
  planId: string;
  testCaseId: string;
  testCaseHumanId: string;
  stepId: string;
  stepNo: number;
  tcode: string | null;
  inputText: string | null;
}

export function DefectCreateDialog({
  projectId,
  boardPlanFilter = '',
  stepContext,
  onClose,
  onCreated,
}: {
  projectId: string;
  boardPlanFilter?: string;
  stepContext?: DefectStepContext;
  onClose: () => void;
  onCreated: (defect: Defect) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contextId = useId();

  // A11y-Nachruestung nach dem Muster von SidePanel/RetestGuardDialog (natives <dialog>, Escape
  // per document-keydown, Fokus rein beim Oeffnen). Wie im RetestGuardDialog in der
  // CAPTURE-Phase samt stopPropagation: sollte der Dialog je ueber einem offenen SidePanel
  // stehen, verbraucht er das Escape vollstaendig, statt beide auf einmal zu schliessen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const [title, setTitle] = useState(() =>
    stepContext
      ? buildAutoDefectTitle({
          testCaseHumanId: stepContext.testCaseHumanId,
          stepNo: stepContext.stepNo,
          tcode: stepContext.tcode,
        })
      : '',
  );
  const [description, setDescription] = useState('');
  const [severityId, setSeverityId] = useState('');
  const [errorTypeId, setErrorTypeId] = useState('');
  const [testCaseId, setTestCaseId] = useState('');

  // #57: Kein useEffect fuer die Vorbelegung. Die Planliste laedt asynchron — ein Effekt, der
  // setPlanId nachzieht, ueberschriebe eine bereits getroffene Auswahl, sobald die Query
  // aufloest. Stattdessen merkt sich der Dialog, OB der Nutzer gewaehlt hat, und leitet den Wert
  // sonst jedes Mal frisch ab.
  const [planTouched, setPlanTouched] = useState(false);
  const [planId, setPlanId] = useState('');

  const { data: config } = useQuery({
    queryKey: ['defect-config', projectId],
    queryFn: () => defectsApi.config(projectId),
    enabled: Boolean(projectId),
  });
  const { data: testCases } = useQuery({
    queryKey: ['catalog-testcases', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
    enabled: Boolean(projectId) && !stepContext,
  });
  const { data: plans } = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId),
    enabled: Boolean(projectId) && !stepContext,
  });

  // Vorrang (Spec §4.2): Board-Filter → Vorrangregel unter den aktiven Plaenen → kein Plan.
  // defaultActivePlanId, NICHT find(p => p.is_active) — seit #55 duerfen mehrere Plaene aktiv
  // sein, und dann gewinnt der mit dem naechsten Zieltermin.
  const prefilledPlanId =
    boardPlanFilter === 'none'
      ? ''
      : boardPlanFilter !== ''
        ? boardPlanFilter
        : (defaultActivePlanId(plans ?? []) ?? '');
  const effectivePlanId = planTouched ? planId : prefilledPlanId;

  const create = useMutation({
    mutationFn: () =>
      stepContext
        ? runsApi.createStepDefect(projectId, stepContext.planId, stepContext.testCaseId, {
            failed_step_id: stepContext.stepId,
            title: title.trim(),
            description: description.trim() || null,
            severity_id: severityId || null,
            error_type_id: errorTypeId || null,
          })
        : defectsApi.create(projectId, {
            title: title.trim(),
            description: description.trim() || null,
            severity_id: severityId || null,
            error_type_id: errorTypeId || null,
            test_case_id: testCaseId || null,
            test_plan_id: effectivePlanId || null,
          }),
    onSuccess: (defect) => {
      void queryClient.invalidateQueries({ queryKey: ['defects', projectId] });
      // #208: Tester-Listen "Meine Defects" und "von mir gemeldet" (lib/my-defects.ts).
      void queryClient.invalidateQueries({
        queryKey: ['my-defects', projectId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['my-reported', projectId],
      });
      // #208: Der neue Fehler ist am Schritt zu markieren (und in „Fehler melden“ wählbar).
      if (stepContext) {
        void queryClient.invalidateQueries({
          queryKey: ['noted-step-defects', projectId, stepContext.planId],
        });
      }
      onClose();
      // #37: Statt auf die Detailroute zu springen, oeffnet das Board den neuen Defect im Panel.
      // Der Dialog kennt das Panel nicht — er meldet nur, was entstanden ist.
      onCreated(defect);
    },
  });

  // #208: Überschrift einmal berechnen — h2 und aria-label des Dialogs sagen dasselbe.
  const heading = stepContext
    ? t('defect.create.stepTitle', { no: stepContext.stepNo })
    : t('defect.create.title');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      {/* Natives <dialog open> statt div[role=dialog] — Begruendung s. side-panel.tsx (kein
          showModal(), Escape behandeln wir selbst im useEffect oben). */}
      <dialog
        ref={dialogRef}
        open
        tabIndex={-1}
        aria-modal="true"
        aria-label={heading}
        // #208: Im Schritt-Modus beschreibt die Kontextzeile den Dialog — der Screenreader liest
        // Testfall, Schritt, T-Code und Input beim Öffnen mit vor.
        aria-describedby={stepContext ? contextId : undefined}
        className="relative z-10 m-0 w-full max-w-lg space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 text-text-body shadow-xl outline-none"
      >
        <h2 className="text-sm font-semibold text-text-primary">{heading}</h2>
        {stepContext && <p className="text-xs text-text-muted">{t('defect.create.stepHint')}</p>}

        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('defect.create.titleField')}
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('defect.create.descField')}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[72px] rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          />
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            {t('defect.filter.severity')}
            <select
              value={severityId}
              onChange={(e) => setSeverityId(e.target.value)}
              className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
            >
              <option value="">{t('defect.severityNone')}</option>
              {(config?.severity_levels ?? []).map((s) => (
                <option key={s.severity_id} value={s.severity_id}>
                  {pickLang(lang, s.label_de, s.label_en)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-text-muted">
            {t('defect.errorType')}
            <select
              value={errorTypeId}
              onChange={(e) => setErrorTypeId(e.target.value)}
              className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
            >
              <option value="">{t('defect.severityNone')}</option>
              {(config?.error_types ?? []).map((et) => (
                <option key={et.error_type_id} value={et.error_type_id}>
                  {pickLang(lang, et.label_de, et.label_en)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {stepContext ? (
          <p
            id={contextId}
            className="rounded-btn bg-bg-subtle px-2 py-1.5 text-xs text-text-muted"
          >
            {[
              t('defect.create.stepContext', {
                testCaseHumanId: stepContext.testCaseHumanId,
                stepNo: stepContext.stepNo,
              }),
              stepContext.tcode,
              stepContext.inputText,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : (
          <>
            <label className="flex flex-col gap-1 text-xs text-text-muted">
              {t('defect.plan')}
              <select
                value={effectivePlanId}
                onChange={(e) => {
                  setPlanTouched(true);
                  setPlanId(e.target.value);
                }}
                className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
              >
                <option value="">{t('defect.noPlan')}</option>
                {(plans ?? []).map((p) => (
                  <option key={p.test_plan_id} value={p.test_plan_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs text-text-muted">
              {t('defect.create.testCase')}
              <select
                value={testCaseId}
                onChange={(e) => setTestCaseId(e.target.value)}
                className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
              >
                <option value="">{t('defect.create.noTestCase')}</option>
                {(testCases ?? []).map((tc) => (
                  <option key={tc.test_case_id} value={tc.test_case_id}>
                    {tc.human_id} {pickLang(lang, tc.title_de, tc.title_en)}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary"
          >
            {t('defect.create.cancel')}
          </button>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={create.isPending || title.trim().length === 0}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('defect.create.submit')}
          </button>
        </div>
      </dialog>
    </div>
  );
}
